import { decodeUtf8 } from './decode.js';
import { describeError, emit, nowMs, } from './debug.js';
import { SoluuDeviceImpl, } from './device.js';
import { SoluuError, toSoluuError } from './errors.js';
import { GRANTED_SERVICES, UUID } from './uuids.js';
export async function requestSoluu(options) {
    const debug = options?.debug;
    if (typeof navigator === 'undefined' || !navigator.bluetooth) {
        throw new SoluuError('GATT_ERROR', 'Web Bluetooth is not available in this environment');
    }
    let device;
    try {
        device = await navigator.bluetooth.requestDevice({
            // Pick by advertised name — the user doesn't know the device's
            // specific name, so we list every Soluu the browser sees and let
            // them pick. GRANTED_SERVICES is an inert permission grant covering
            // every service we touch; it is not an advertisement scan.
            filters: [{ namePrefix: 'SOLUU' }],
            optionalServices: GRANTED_SERVICES,
        });
    }
    catch (cause) {
        throw toSoluuError(cause);
    }
    if (!device.gatt) {
        throw new SoluuError('NOT_CONNECTED', 'Device has no GATT server');
    }
    emit(debug, {
        type: 'gatt:connecting',
        deviceId: device.id,
        deviceName: device.name ?? '',
        atMs: nowMs(),
    });
    let server;
    try {
        server = await device.gatt.connect();
    }
    catch (cause) {
        throw toSoluuError(cause, 'NOT_CONNECTED');
    }
    emit(debug, {
        type: 'gatt:connected',
        deviceId: device.id,
        deviceName: device.name ?? '',
        atMs: nowMs(),
    });
    // ---------------------------------------------------------------------
    // Tiers 1 and 3 — STRICT. Anything in here failing means the harness
    // cannot do its core job (WiFi diagnosis), so the connect is torn down
    // and the error rethrown. This block's behaviour is unchanged.
    // ---------------------------------------------------------------------
    let strict;
    try {
        strict = await resolveStrict(server, debug);
    }
    catch (cause) {
        try {
            device.gatt.disconnect();
        }
        catch {
            /* ignore */
        }
        throw toSoluuError(cause);
    }
    // ---------------------------------------------------------------------
    // Tiers 2 and 4 — TOLERANT. Structurally past the disconnect-and-rethrow
    // block above: every call below traps its own failure and degrades one
    // capability to null. One missing characteristic on some firmware build
    // must never collapse the connect and take WiFi testing down with it
    // (W-002).
    //
    // Tolerant does NOT mean optional. §211–§214, §414 and the DIS strings are
    // required by firmware; they are discovered tolerantly for diagnosability,
    // because a strict miss kills the connect without saying WHICH
    // characteristic killed it — in a tool whose whole job is answering that.
    // So the connect survives and the harness is loud about the miss.
    // ---------------------------------------------------------------------
    const missingExpected = [];
    const resolveOptional = async (service, uuid, name, severity, absentServiceReason) => {
        const charUuid = formatUuid(uuid);
        const noteMiss = (error) => {
            emit(debug, {
                type: 'characteristic:missing',
                charUuid,
                name,
                severity,
                error,
                atMs: nowMs(),
            });
            if (severity === 'expected') {
                missingExpected.push({ uuid: charUuid, name });
            }
        };
        if (!service) {
            noteMiss(absentServiceReason ?? 'Owning service was not found on this device');
            return null;
        }
        try {
            const char = await service.getCharacteristic(uuid);
            emit(debug, { type: 'characteristic:resolved', charUuid, atMs: nowMs() });
            return char;
        }
        catch (cause) {
            noteMiss(describeError(cause));
            return null;
        }
    };
    // Tier 2 — tolerant service. The Battery Service is absent from the GATT
    // specification document (D-011) though firmware implements it, so treat
    // its absence as survivable.
    const batteryService = await resolveOptionalService(server, UUID.batteryService, debug);
    // Tier 4 — tolerant characteristics, resolved SEQUENTIALLY. Chrome
    // serializes GATT operations anyway so Promise.all buys nothing here, and
    // session 0006's Promise.all reject path is precisely what force-disconnected
    // GATT once already.
    const info = strict.infoService;
    const manufacturerChar = await resolveOptional(info, UUID.manufacturerNameString, 'Manufacturer Name String', 'expected');
    const modelChar = await resolveOptional(info, UUID.modelNumberString, 'Model Number String', 'expected');
    // Conditional and silent: §2A25 is on Chrome's Web Bluetooth GATT blocklist
    // and rejects with SecurityError in every browser client (W-006). Requested
    // anyway so a future blocklist relaxation works with no code change.
    const serialChar = await resolveOptional(info, UUID.serialNumberString, 'Serial Number String', 'conditional');
    const softwareRevisionChar = await resolveOptional(info, UUID.softwareRevisionString, 'Software Revision String', 'expected');
    // §2A26 and §414 move from the strict tier to this one. Neither is required
    // for WiFi diagnosis, and §414 is a newly-corrected handle — leaving them
    // strict would reintroduce exactly the fragility this tier removes.
    const firmwareChar = await resolveOptional(info, UUID.firmwareRevisionString, 'Firmware Revision String', 'expected');
    const radioFirmwareChar = await resolveOptional(strict.otaService, UUID.radioFirmwareVersion, 'Radio FW Version', 'expected');
    const batteryLevel = await resolveOptional(batteryService, UUID.batteryLevel, 'Battery Level', 'expected', 'Battery Service (0x180f) was not found on this device');
    const pulseIntensity = await resolveOptional(strict.configService, UUID.pulseIntensity, 'Pulse Intensity', 'expected');
    const volumeEnabled = await resolveOptional(strict.configService, UUID.volumeEnabled, 'Volume Enabled', 'expected');
    const volumeLevel = await resolveOptional(strict.configService, UUID.volumeLevel, 'Volume Level', 'expected');
    const vibrationEnabled = await resolveOptional(strict.configService, UUID.vibrationEnabled, 'Vibration Enabled', 'expected');
    // Conditional and silent: §315 entered firmware at fw_rev 0.250514, so
    // older units simply don't have it. Presence of the characteristic is the
    // ONLY gate — never a firmware-version comparison (W-011).
    const wifiError = await resolveOptional(strict.wifiService, UUID.wifiError, 'WiFi Error', 'conditional');
    // Identity strings are read at connect through a trapped read: whatever the
    // device returns, verbatim after the existing NUL/whitespace trim. `null`
    // means could-not-read; `''` means the device reported empty.
    const manufacturer = await readUtf8OrNull(manufacturerChar, debug);
    const model = await readUtf8OrNull(modelChar, debug);
    const serialNumber = await readUtf8OrNull(serialChar, debug);
    const softwareRevision = await readUtf8OrNull(softwareRevisionChar, debug);
    const firmwareVersion = await readUtf8OrNull(firmwareChar, debug);
    const radioFirmwareVersion = await readUtf8OrNull(radioFirmwareChar, debug);
    // Battery and configuration are deliberately NOT read at connect —
    // readBattery() / readConfiguration() are on demand.
    const chars = {
        wifiStatus: strict.wifiStatus,
        wifiSsid: strict.wifiSsid,
        wifiPassword: strict.wifiPassword,
        wifiSecurity: strict.wifiSecurity,
        wifiConnect: strict.wifiConnect,
        wifiPingEndpoint: strict.wifiPingEndpoint,
        pulseIntensity,
        volumeEnabled,
        volumeLevel,
        vibrationEnabled,
        wifiError,
        batteryLevel,
    };
    // Construct the device FIRST so the characteristicvaluechanged listeners
    // are attached before notifications are enabled. addEventListener does
    // not replay, so the reverse order would drop any notification that fires
    // between startNotifications resolving and the listener attaching.
    const soluuDevice = new SoluuDeviceImpl(device, chars, {
        name: device.name ?? '',
        mac: device.id,
        firmwareVersion,
        radioFirmwareVersion,
        manufacturer,
        model,
        serialNumber,
        softwareRevision,
    }, missingExpected, debug);
    // §216 WiFi Status notifications are the harness's core function. A failure
    // here is fatal to the connect — unchanged.
    try {
        await strict.wifiStatus.startNotifications();
        emit(debug, {
            type: 'notify:subscribed',
            charUuid: strict.wifiStatus.uuid,
            atMs: nowMs(),
        });
    }
    catch (cause) {
        emit(debug, {
            type: 'notify:error',
            charUuid: strict.wifiStatus.uuid,
            error: describeError(cause),
            atMs: nowMs(),
        });
        try {
            await soluuDevice.disconnect();
        }
        catch {
            /* ignore */
        }
        throw toSoluuError(cause);
    }
    // §315 WiFi Error notifications are diagnostic, not core. Unlike §216, a
    // failed subscribe here must NOT disconnect — it degrades to read-on-demand.
    // Whether §315 actually notifies is unconfirmed on hardware (W-011); this
    // path is how the bench answers it either way.
    if (wifiError) {
        try {
            await wifiError.startNotifications();
            emit(debug, {
                type: 'notify:subscribed',
                charUuid: wifiError.uuid,
                atMs: nowMs(),
            });
        }
        catch (cause) {
            emit(debug, {
                type: 'notify:error',
                charUuid: wifiError.uuid,
                error: describeError(cause),
                atMs: nowMs(),
            });
        }
    }
    return soluuDevice;
}
// Tier 1 (services) and tier 3 (characteristics). "Strict" is about
// DISCOVERY, not access: the handle must resolve or the connect fails. It
// says nothing about which GATT properties are usable — §312 is the case in
// point. Its handle is required because the harness writes it on every
// attempt, while whether it can be READ is decided separately by
// `canReadPassword` from the characteristic's `read` property. Write
// required, read optional.
async function resolveStrict(server, debug) {
    const [configService, wifiService, otaService, infoService] = await Promise.all([
        resolveService(server, UUID.configurationService, debug),
        resolveService(server, UUID.wifiService, debug),
        resolveService(server, UUID.otaService, debug),
        resolveService(server, UUID.deviceInformationService, debug),
    ]);
    const [wifiStatus, wifiSsid, wifiPassword, wifiSecurity, wifiConnect, wifiPingEndpoint,] = await Promise.all([
        resolveCharacteristic(configService, UUID.wifiStatus, debug),
        resolveCharacteristic(wifiService, UUID.wifiSsid, debug),
        resolveCharacteristic(wifiService, UUID.wifiPassword, debug),
        resolveCharacteristic(wifiService, UUID.wifiSecurity, debug),
        resolveCharacteristic(wifiService, UUID.wifiConnect, debug),
        resolveCharacteristic(configService, UUID.wifiPingEndpoint, debug),
    ]);
    return {
        configService,
        wifiService,
        otaService,
        infoService,
        wifiStatus,
        wifiSsid,
        wifiPassword,
        wifiSecurity,
        wifiConnect,
        wifiPingEndpoint,
    };
}
// Format a UUID for diagnostics. Soluu UUIDs are full strings already;
// standard 16-bit aliases come through as numbers (e.g. 0x180a) and would
// otherwise stringify to confusing decimal ("6154") in the log.
function formatUuid(uuid) {
    return typeof uuid === 'number' ? `0x${uuid.toString(16).padStart(4, '0')}` : uuid;
}
async function resolveService(server, uuid, debug) {
    try {
        const service = await server.getPrimaryService(uuid);
        emit(debug, { type: 'service:resolved', serviceUuid: formatUuid(uuid), atMs: nowMs() });
        return service;
    }
    catch (cause) {
        emit(debug, {
            type: 'service:error',
            serviceUuid: formatUuid(uuid),
            error: describeError(cause),
            atMs: nowMs(),
        });
        throw cause;
    }
}
async function resolveOptionalService(server, uuid, debug) {
    try {
        const service = await server.getPrimaryService(uuid);
        emit(debug, { type: 'service:resolved', serviceUuid: formatUuid(uuid), atMs: nowMs() });
        return service;
    }
    catch (cause) {
        // Recorded, never rethrown — the per-characteristic miss below carries
        // the severity, so this row is the mechanism and that row is the verdict.
        emit(debug, {
            type: 'service:error',
            serviceUuid: formatUuid(uuid),
            error: describeError(cause),
            atMs: nowMs(),
        });
        return null;
    }
}
async function resolveCharacteristic(service, uuid, debug) {
    try {
        const char = await service.getCharacteristic(uuid);
        emit(debug, {
            type: 'characteristic:resolved',
            charUuid: formatUuid(uuid),
            atMs: nowMs(),
        });
        return char;
    }
    catch (cause) {
        emit(debug, {
            type: 'characteristic:error',
            charUuid: formatUuid(uuid),
            error: describeError(cause),
            atMs: nowMs(),
        });
        throw cause;
    }
}
// A resolved characteristic whose read fails is a different failure from one
// that never resolved, so it is NOT recorded as missing — but it is not
// swallowed either. `characteristic:error` carries the reason into
// Diagnostics, where it renders error-styled.
async function readUtf8OrNull(char, debug) {
    if (!char)
        return null;
    try {
        return decodeUtf8(await char.readValue());
    }
    catch (cause) {
        emit(debug, {
            type: 'characteristic:error',
            charUuid: char.uuid,
            error: `read failed: ${describeError(cause)}`,
            atMs: nowMs(),
        });
        return null;
    }
}
//# sourceMappingURL=request.js.map